import { i18n } from '@apps/main/modules/localization/localizationSetup';

import type { ActivityStaticData, Distance } from '../models';

const { t } = i18n.global;

export function getButtonText(showFullDescription: boolean): string {
  return showFullDescription ? t('planner.show_less') : t('planner.show_more');
}

export function getDistances(
  activityDetails: ActivityStaticData | undefined
): Distance[] | undefined {
  if (!activityDetails) {
    return;
  }
  const distanceValues = Object.values(
    activityDetails.staticInfo.distances ?? {}
  );

  if (distanceValues.length === 0) {
    return;
  }

  return distanceValues
    .map((value) => ({
      description: value.description,
      distanceMeters: value.value,
    }))
    .sort((a, b) => a.distanceMeters - b.distanceMeters);
}

export function getFacilities(
  activityDetails: ActivityStaticData | undefined
): string[] | undefined {
  if (!activityDetails) {
    return;
  }
  const propertyArray = Object.values(
    activityDetails.staticInfo.properties ?? {}
  );

  if (propertyArray.length === 0) {
    return;
  }

  return propertyArray
    .filter((facility) => facility.value)
    .map((facility) => facility.description);
}

// NOTE:
// In collapsed version of activityDetails description we want to show more or less the amount of text
// equal to height of map in the other column. Database content is not consistent (HTML, Markdown, optional fields),
// so calculating this exactly is hard. Fuzzy logic below is an approximation.
export function getMainDescription(
  activityDetails: ActivityStaticData | undefined,
  showFullDescription: boolean,
  tagsUrls: string[] | undefined,
  charactersConfig: { tagsEquivalent: number; toFillSpaceNextToMap: number }
): string | undefined {
  function trimDescription(
    fullDescription: string,
    allowedCharacters: number
  ): string {
    const sliced = fullDescription.slice(0, allowedCharacters);
    return `${sliced}...`;
  }
  const fullDescription = activityDetails?.staticInfo?.description?.value;

  if (!fullDescription) {
    return;
  }

  let descriptionLength = charactersConfig.toFillSpaceNextToMap;

  if (tagsUrls?.length) {
    descriptionLength -= charactersConfig.tagsEquivalent;
  }

  return showFullDescription
    ? fullDescription
    : trimDescription(fullDescription, descriptionLength);
}

export function getOpeningParagraph(
  activityDetails: ActivityStaticData | undefined
): string | undefined {
  if (!activityDetails) {
    return;
  }

  const openingParagraph = activityDetails.staticInfo.openingParagraph;

  if (!openingParagraph || !openingParagraph.value) {
    return;
  }

  return openingParagraph.value;
}

export function getTagsUrls(
  activityDetails: ActivityStaticData | undefined
): string[] | undefined {
  if (!activityDetails) {
    return;
  }
  return activityDetails.staticInfo.tagIconUrls;
}
